// CTE scope resolver. Walks the leading `WITH …` clause of a single
// statement and collects the visible CTE bindings — name, optional explicit
// column list, recursive flag, definition range. Source-text driven (token
// stream from `tokenize`) so trailing-cursor cases survive tree-sitter's
// awkward error recovery.
//
// Acceptance contract (PRD: Slice "Alias + CTE Scope Resolver"):
//   • multi-CTE (`WITH a AS (…), b AS (…)`)
//   • RECURSIVE flag (PG/MSSQL/MySQL/SQLite)
//   • explicit column-list form (`WITH foo (c1, c2) AS (…)`)
//   • PG `MATERIALIZED` / `NOT MATERIALIZED` modifier between AS and `(`
//
// PostgreSQL / SQLite extension (PRD: Slice "PostgreSQL Resolver — CTEs +
// Subqueries"):
//   • DML-in-CTE RETURNING — when the CTE body is INSERT/UPDATE/DELETE/MERGE
//     with a RETURNING clause and no explicit column list was declared, the
//     column names from RETURNING are exposed via `columns` so downstream
//     `SELECT cte.|` completion can surface them.
//
// Inner SELECT body of each CTE is intentionally NOT walked here — the
// CTE's columns are either named in the explicit list, the body's RETURNING
// clause, or inferred from the inner SELECT by a later slice.

import type {
  CteBinding,
  DatabaseEngineId,
  OffsetRange,
} from '../../../core/engines';
import { tokenize, type Token } from '../context/tokenizer';

import { parseIdentifierToken } from './identifierUtils';
import { extractReturningColumns } from './returningExtractor';

export function resolveCteBindings(
  source: string,
  statementRange: OffsetRange,
  engineId: DatabaseEngineId,
): readonly CteBinding[] {
  const tokens = tokenize(source, statementRange.start, statementRange.end, engineId);
  if (tokens.length === 0) return [];
  const withIdx = findCteWithIndex(tokens);
  if (withIdx === -1) return [];

  let i = withIdx + 1;
  let recursive = false;
  if (tokens[i] && tokens[i].type === 'kw' && tokens[i].text.toUpperCase() === 'RECURSIVE') {
    recursive = true;
    i += 1;
  }

  const bindings: CteBinding[] = [];
  while (i < tokens.length) {
    const cteResult = parseSingleCte(tokens, i, recursive);
    if (!cteResult) break;
    bindings.push(cteResult.binding);
    i = cteResult.nextIdx;
    if (tokens[i] && tokens[i].type === 'comma') {
      i += 1;
      continue;
    }
    break;
  }

  return bindings;
}

/**
 * Index of the `WITH` that opens the statement's CTE prolog, or -1.
 *
 * Normally token 0 — but a CTE prolog is equally legal inside a view / CTAS
 * body (`CREATE OR ALTER VIEW v … AS WITH x AS (…) SELECT …`, supported by all
 * five engines), and there the statement leads with CREATE. Requiring token 0
 * left every alias bound to such a CTE unresolvable: `pr.▮` inside the view
 * body completed to nothing at all (mssql script.32 §7).
 *
 * The body form is recognised as `AS` immediately followed by `WITH`, which is
 * what makes it unambiguous: T-SQL's view options (`WITH SCHEMABINDING`) and a
 * table hint (`WITH (NOLOCK)`) never sit directly after the body's `AS`, and a
 * non-CTE `WITH` there parses to zero bindings anyway (the scan below requires
 * `<name> AS (`).
 */
function findCteWithIndex(tokens: readonly Token[]): number {
  const head = tokens[0];
  if (!head) return -1;
  if (head.type === 'kw' && head.text.toUpperCase() === 'WITH') return 0;
  const headUpper = head.text.toUpperCase();
  if (head.type !== 'kw' && head.type !== 'ident') return -1;
  if (headUpper !== 'CREATE' && headUpper !== 'ALTER') return -1;
  for (let i = 1; i < tokens.length - 1; i += 1) {
    if (tokens[i]!.text.toUpperCase() !== 'AS') continue;
    const next = tokens[i + 1]!;
    if (next.text.toUpperCase() === 'WITH') return i + 1;
  }
  return -1;
}

type CteParseResult = { readonly binding: CteBinding; readonly nextIdx: number };

function parseSingleCte(
  tokens: readonly Token[],
  startIdx: number,
  recursive: boolean,
): CteParseResult | null {
  const nameTok = tokens[startIdx];
  if (!nameTok || (nameTok.type !== 'ident' && nameTok.type !== 'kw')) return null;
  const parsedName = parseIdentifierToken(nameTok.text);
  if (parsedName.content.length === 0) return null;

  let i = startIdx + 1;
  let columns: string[] | undefined;
  if (tokens[i] && tokens[i].type === 'lparen') {
    const listResult = parseColumnList(tokens, i);
    if (!listResult) return null;
    columns = listResult.columns;
    i = listResult.nextIdx;
  }

  if (!tokens[i] || tokens[i].type !== 'kw' || tokens[i].text.toUpperCase() !== 'AS') {
    return null;
  }
  i += 1;

  // Optional MATERIALIZED / NOT MATERIALIZED.
  if (tokens[i] && tokens[i].type === 'kw') {
    const kw = tokens[i].text.toUpperCase();
    if (kw === 'NOT' && tokens[i + 1] && tokens[i + 1].type === 'kw' && tokens[i + 1].text.toUpperCase() === 'MATERIALIZED') {
      i += 2;
    } else if (kw === 'MATERIALIZED') {
      i += 1;
    }
  }

  if (!tokens[i] || tokens[i].type !== 'lparen') return null;
  const bodyStart = i;
  const bodyEnd = findMatchingRparen(tokens, bodyStart);
  if (bodyEnd === -1) {
    // Unclosed paren (cursor mid-buffer) — synthesize an open-ended range so
    // the binding still surfaces; the next CTE in the comma list is unlikely
    // to be parseable, so we'll bail after emitting this one. The body
    // continues past the last seen token (statement-isolation trims trailing
    // whitespace from `statementEnd`, so the cursor may sit past it on
    // trailing whitespace — `... FROM |`). Use an open-ended sentinel so
    // any cursor position past `bodyStart` still tests as "inside" for
    // F5.2-CB8 non-recursive self-reference suppression.
    const definedAt: OffsetRange = { start: nameTok.start, end: tokens[tokens.length - 1].end };
    const bodyRange: OffsetRange = { start: tokens[bodyStart].start, end: Number.MAX_SAFE_INTEGER };
    return {
      binding: { name: parsedName.content, columns, recursive, definedAt, bodyRange },
      nextIdx: tokens.length,
    };
  }
  // If no explicit column list was provided, fall back to RETURNING column
  // names extracted from the body (DML-in-CTE pattern, PG/SQLite). When
  // both are present, the explicit list wins (it renames the RETURNING
  // outputs from the caller's perspective).
  const finalColumns = columns ?? extractReturningColumns(tokens, bodyStart, bodyEnd);
  const definedAt: OffsetRange = { start: nameTok.start, end: tokens[bodyEnd].end };
  const bodyRange: OffsetRange = { start: tokens[bodyStart].start, end: tokens[bodyEnd].end };
  return {
    binding: { name: parsedName.content, columns: finalColumns, recursive, definedAt, bodyRange },
    nextIdx: bodyEnd + 1,
  };
}

type ColumnListResult = { readonly columns: string[]; readonly nextIdx: number };

function parseColumnList(tokens: readonly Token[], lparenIdx: number): ColumnListResult | null {
  const columns: string[] = [];
  let i = lparenIdx + 1;
  while (i < tokens.length) {
    const tok = tokens[i];
    if (tok.type === 'rparen') return { columns, nextIdx: i + 1 };
    if (tok.type === 'ident' || tok.type === 'kw') {
      columns.push(parseIdentifierToken(tok.text).content);
      i += 1;
      continue;
    }
    if (tok.type === 'comma') {
      i += 1;
      continue;
    }
    return null;
  }
  return null;
}

function findMatchingRparen(tokens: readonly Token[], lparenIdx: number): number {
  let depth = 0;
  for (let i = lparenIdx; i < tokens.length; i += 1) {
    if (tokens[i].type === 'lparen') depth += 1;
    else if (tokens[i].type === 'rparen') {
      depth -= 1;
      if (depth === 0) return i;
    }
  }
  return -1;
}

